import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const CommissionPayoutModal = ({ isOpen, onClose, agent }) => {
  const [payoutMethod, setPayoutMethod] = useState('bank_transfer');
  const [selectedAccount, setSelectedAccount] = useState('');
  const [requestAmount, setRequestAmount] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Mock agent commission data
  const commissionData = {
    availableBalance: agent?.commissions?.pending || 1250.75,
    minimumPayout: 100.00,
    processingFee: 2.50,
    estimatedArrival: '2-3 business days',
    currency: 'USD'
  };

  const payoutMethods = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'paypal', label: 'PayPal' },
    { value: 'check', label: 'Physical Check' },
    { value: 'wire', label: 'Wire Transfer' }
  ];

  const savedAccounts = [
    { 
      value: 'account_1', 
      label: 'Bank of America - ***4532',
      type: 'bank_transfer',
      details: 'Checking Account'
    },
    { 
      value: 'account_2', 
      label: 'Chase Bank - ***7891',
      type: 'bank_transfer',
      details: 'Savings Account'
    },
    { 
      value: 'paypal_1', 
      label: 'michael.rodriguez@email.com',
      type: 'paypal',
      details: 'PayPal Account'
    }
  ];

  const getFilteredAccounts = () => {
    return savedAccounts?.filter(acc => acc?.type === payoutMethod);
  };

  const handleRequestPayout = () => {
    const amount = parseFloat(requestAmount);
    if (amount < commissionData?.minimumPayout) {
      alert(`Minimum payout amount is $${commissionData?.minimumPayout}`);
      return;
    }
    if (amount > commissionData?.availableBalance) {
      alert('Requested amount exceeds available balance');
      return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmPayout = () => {
    console.log('Confirming payout:', {
      amount: requestAmount,
      method: payoutMethod,
      account: selectedAccount
    });
    // Handle payout confirmation
    onClose?.();
  };

  const getNetAmount = () => {
    const amount = parseFloat(requestAmount) || 0;
    const net = amount - commissionData?.processingFee;
    return net > 0 ? net?.toFixed(2) : '0.00';
  };

  const getProcessingFeePercentage = () => {
    const amount = parseFloat(requestAmount) || 0;
    if (amount === 0) return '0';
    const percentage = (commissionData?.processingFee / amount) * 100;
    return percentage?.toFixed(2);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-semibold text-foreground">Request Commission Payout</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Withdraw your earned commissions to your preferred account
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {!showConfirmation ? (
          <>
            {/* Available Balance */}
            <div className="p-6 border-b border-border">
              <div className="bg-gradient-to-r from-success/5 to-success/10 border border-success/20 rounded-lg p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-sm text-success/80 mb-1">Available for Withdrawal</p>
                    <p className="text-4xl font-bold text-success">
                      ${commissionData?.availableBalance?.toFixed(2)}
                    </p>
                  </div>
                  <div className="w-16 h-16 bg-success/10 rounded-full flex items-center justify-center">
                    <Icon name="DollarSign" size={32} className="text-success" />
                  </div>
                </div>
                <div className="flex items-center space-x-4 text-sm text-success/70">
                  <div className="flex items-center space-x-1">
                    <Icon name="Info" size={14} />
                    <span>Minimum: ${commissionData?.minimumPayout?.toFixed(2)}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Clock" size={14} />
                    <span>{commissionData?.estimatedArrival}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Payout Form */}
            <div className="p-6 space-y-4">
              {/* Payout Method */}
              <Select
                label="Payout Method"
                options={payoutMethods}
                value={payoutMethod}
                onChange={(e) => {
                  setPayoutMethod(e?.target?.value);
                  setSelectedAccount('');
                }}
                required
              />

              {/* Account Selection */}
              <Select
                label="Select Account"
                options={getFilteredAccounts()}
                value={selectedAccount}
                onChange={(e) => setSelectedAccount(e?.target?.value)}
                placeholder="Choose an account"
                required
              />

              {/* Quick Action: Request All */}
              <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                <div className="text-sm">
                  <p className="font-medium text-foreground">Quick Request</p>
                  <p className="text-muted-foreground">Request entire available balance</p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setRequestAmount(commissionData?.availableBalance?.toString())}
                >
                  Request All
                </Button>
              </div>

              {/* Amount Input */}
              <Input
                type="number"
                label="Payout Amount"
                value={requestAmount}
                onChange={(e) => setRequestAmount(e?.target?.value)}
                placeholder="0.00"
                prefix="$"
                min={commissionData?.minimumPayout}
                max={commissionData?.availableBalance}
                required
              />

              {/* Fee Breakdown */}
              {requestAmount && (
                <div className="bg-muted/20 rounded-lg p-4 space-y-2">
                  <h4 className="font-semibold text-foreground mb-3">Payout Breakdown</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Requested Amount:</span>
                    <span className="text-foreground font-medium">
                      ${parseFloat(requestAmount)?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">
                      Processing Fee ({getProcessingFeePercentage()}%):
                    </span>
                    <span className="text-foreground font-medium">
                      -${commissionData?.processingFee?.toFixed(2)}
                    </span>
                  </div>
                  <div className="border-t border-border pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-medium text-foreground">Net Amount:</span>
                      <span className="font-bold text-foreground text-lg">
                        ${getNetAmount()}
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Important Information */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="Info" size={20} className="text-blue-600 mt-0.5" />
                  <div className="text-sm text-blue-900 space-y-2">
                    <p className="font-medium">Important Information</p>
                    <ul className="list-disc list-inside space-y-1 text-blue-800">
                      <li>Payouts are processed within {commissionData?.estimatedArrival}</li>
                      <li>A ${commissionData?.processingFee?.toFixed(2)} processing fee applies to all payouts</li>
                      <li>Minimum payout amount is ${commissionData?.minimumPayout?.toFixed(2)}</li>
                      <li>Ensure your account information is accurate to avoid delays</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Add New Account */}
              <div className="border border-dashed border-border rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h4 className="font-semibold text-foreground mb-1">Need a Different Account?</h4>
                    <p className="text-sm text-muted-foreground">
                      Add a new bank account or payment method
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    iconName="Plus"
                    iconPosition="left"
                    onClick={() => console.log('Add new account')}
                  >
                    Add Account
                  </Button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={onClose}
              >
                Cancel
              </Button>
              
              <Button
                onClick={handleRequestPayout}
                iconName="Send"
                iconPosition="left"
                disabled={
                  !requestAmount || 
                  !selectedAccount || 
                  parseFloat(requestAmount) < commissionData?.minimumPayout ||
                  parseFloat(requestAmount) > commissionData?.availableBalance
                }
              >
                Request Payout
              </Button>
            </div>
          </>
        ) : (
          <>
            {/* Confirmation Screen */}
            <div className="p-6 space-y-6">
              <div className="text-center space-y-4">
                <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto">
                  <Icon name="CheckCircle" size={40} className="text-success" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-foreground mb-2">Confirm Payout Request</h3>
                  <p className="text-muted-foreground">
                    Please review the details before confirming your payout request
                  </p>
                </div>
              </div>

              {/* Payout Summary */}
              <div className="bg-card border border-border rounded-lg p-6 space-y-4">
                <h4 className="font-semibold text-foreground">Payout Summary</h4>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Payout Method:</span>
                    <span className="text-foreground font-medium">
                      {payoutMethods?.find(m => m?.value === payoutMethod)?.label}
                    </span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Account:</span>
                    <span className="text-foreground font-medium">
                      {savedAccounts?.find(a => a?.value === selectedAccount)?.label}
                    </span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Requested Amount:</span>
                    <span className="text-foreground font-medium">
                      ${parseFloat(requestAmount)?.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between py-2 border-b border-border">
                    <span className="text-muted-foreground">Processing Fee:</span>
                    <span className="text-foreground font-medium">
                      -${commissionData?.processingFee?.toFixed(2)}
                    </span>
                  </div>
                  
                  <div className="flex justify-between py-3 bg-success/5 -mx-6 px-6">
                    <span className="font-semibold text-foreground">You Will Receive:</span>
                    <span className="font-bold text-success text-lg">
                      ${getNetAmount()}
                    </span>
                  </div>
                </div>
              </div>

              {/* Confirmation Warning */}
              <div className="bg-warning/10 border border-warning/20 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <Icon name="AlertTriangle" size={20} className="text-warning mt-0.5" />
                  <div className="text-sm text-warning">
                    <p className="font-medium mb-1">Processing Time</p>
                    <p>Your payout will be processed within {commissionData?.estimatedArrival}. 
                    Please ensure your account information is correct as changes cannot be made after confirmation.</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Confirmation Footer */}
            <div className="flex items-center justify-between p-6 border-t border-border">
              <Button
                variant="outline"
                onClick={() => setShowConfirmation(false)}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back
              </Button>
              
              <Button
                onClick={handleConfirmPayout}
                iconName="Check"
                iconPosition="left"
              >
                Confirm Payout
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CommissionPayoutModal;